// ============================================================
// OpsCom — Workforce Pathways & Governance Engine
// Defines all role-based onboarding configurations:
//   • document requirements
//   • competency declarations
//   • registration body validation
//   • compliance rules
//   • governance declarations
//   • operational permissions
//   • CQC & regulatory alignment
// ============================================================

// ─── Pathway Types ────────────────────────────────────────────────────────────
export type WorkforcePathway =
  | "clinical"
  | "fire_rescue"
  | "event_ops"
  | "command_control"
  | "specialist_ops";

// ─── Role Types ───────────────────────────────────────────────────────────────
export type WorkforceRole =
  // Clinical
  | "first_responder"
  | "eca"
  | "technician"
  | "frec3"
  | "frec4"
  | "paramedic"
  | "advanced_paramedic"
  | "critical_care_paramedic"
  | "nurse"
  | "doctor"
  | "consultant"
  // Fire & Rescue
  | "firefighter"
  | "fire_oic"
  | "ba_operator"
  | "water_rescue_technician"
  | "rope_rescue_technician"
  | "hazmat_technician"
  // Event Operations
  | "event_steward"
  | "event_supervisor"
  | "event_medic"
  | "event_commander"
  // Command & Control
  | "dispatcher"
  | "loggist"
  | "bronze_commander"
  | "silver_commander"
  | "gold_commander"
  | "liaison_officer"
  // Specialist
  | "urban_search_rescue"
  | "swift_water_rescue"
  | "wildfire_specialist"
  | "hazmat_specialist";

// ─── Risk Levels ─────────────────────────────────────────────────────────────
export type OperationalRiskLevel = "low" | "medium" | "high" | "critical";

// ─── Vetting Level ───────────────────────────────────────────────────────────
export type VetLevel =
  | "basic_dbs"
  | "standard_dbs"
  | "enhanced_dbs"
  | "enhanced_barred"
  | "sc"  // Security Clearance
  | "dv"; // Developed Vetting

// ─── Document Requirements ───────────────────────────────────────────────────
export type DocumentGdprCategory =
  | "identity"
  | "professional"
  | "health"
  | "criminal"
  | "employment"
  | "qualification"
  | "financial";

export interface RequiredDocument {
  id: string;
  name: string;
  description: string;
  required: boolean;
  validityYears?: number;       // If the document expires
  gdprCategory: DocumentGdprCategory;
  notes?: string;
  verificationRequired?: boolean; // Must be verified by reviewer, not self-declared
}

// ─── Competency Requirements ─────────────────────────────────────────────────
export type CompetencyCategory =
  | "clinical"
  | "rescue"
  | "driving"
  | "command"
  | "safeguarding"
  | "technical"
  | "operational"
  | "communication";

export interface CompetencyRequirement {
  id: string;
  label: string;
  category: CompetencyCategory;
  required: boolean;
  expiryYears?: number;
  notes?: string;
}

// ─── Governance Declarations ─────────────────────────────────────────────────
export type DeclarationType =
  | "gdpr"
  | "clinical_governance"
  | "safeguarding"
  | "conduct"
  | "vetting"
  | "health"
  | "accuracy"
  | "information_security"
  | "conflict_of_interest";

export interface DeclarationRequirement {
  id: string;
  title: string;
  text: string;
  type: DeclarationType;
  required: boolean;
}

// ─── Registration Body Configuration ─────────────────────────────────────────
export interface RegistrationRequirement {
  body: string;
  bodyLabel: string;
  numberLabel: string;
  expiryRequired: boolean;
  verificationUrl?: string;
  notes?: string;
}

// ─── Onboarding Stage ────────────────────────────────────────────────────────
export interface OnboardingStage {
  id: string;
  label: string;
  description: string;
  approvalRequired: boolean;
  approver?: string;
}

// ─── Operational Permissions ─────────────────────────────────────────────────
export interface OperationalPermissions {
  cadAccess: boolean;
  mdtAccess: boolean;
  fleetAccess: boolean;
  commandAccess: boolean;
  commandTier?: "bronze" | "silver" | "gold";
  deploymentEligible: boolean;
  schedulingAccess: boolean;
  clinicalAuditAccess: boolean;
}

// ─── Pathway Config ──────────────────────────────────────────────────────────
export interface PathwayConfig {
  id: WorkforcePathway;
  label: string;
  description: string;
  tagline: string;
  icon: string;            // Emoji / icon key
  color: string;           // Tailwind color class
  bg: string;
  border: string;
  cqcAligned: boolean;
  roles: WorkforceRole[];
}

// ─── Role Config ─────────────────────────────────────────────────────────────
export interface RoleConfig {
  id: WorkforceRole;
  title: string;
  description: string;
  pathway: WorkforcePathway;
  riskLevel: OperationalRiskLevel;
  registration?: RegistrationRequirement;
  requiredDocuments: RequiredDocument[];
  competencies: CompetencyRequirement[];
  declarations: DeclarationRequirement[];
  onboardingStages: OnboardingStage[];
  permissions: OperationalPermissions;
  vetLevel: VetLevel;
  cqcRelevant: boolean;
  complianceScore: number;    // Baseline score when all requirements met (0-100)
  tags: string[];
}

// ─── SHARED DECLARATIONS ─────────────────────────────────────────────────────
const GDPR_DECLARATION: DeclarationRequirement = {
  id: "gdpr",
  title: "Data Processing Consent (UK GDPR)",
  text: "I consent to OpsCom processing my personal data, including sensitive personal data such as criminal records information and health data, for the purposes of recruitment, employment verification, and operational workforce management in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. I understand that my data will be retained in accordance with the OpsCom Data Retention Policy and that I have the right to access, rectify, or erase my data subject to legal obligations.",
  type: "gdpr",
  required: true,
};

const ACCURACY_DECLARATION: DeclarationRequirement = {
  id: "accuracy",
  title: "Declaration of Accuracy",
  text: "I declare that all information provided in this application is true, complete, and accurate to the best of my knowledge. I understand that any false or misleading information may result in my application being rejected, or, if discovered after appointment, may result in summary dismissal and referral to relevant regulatory bodies.",
  type: "accuracy",
  required: true,
};

const CONDUCT_DECLARATION: DeclarationRequirement = {
  id: "conduct",
  title: "Code of Conduct Agreement",
  text: "I agree to comply with the OpsCom Staff Code of Conduct, including requirements relating to professional behaviour, confidentiality, information security, and the safe and lawful discharge of my duties. I acknowledge that breaches of this code may result in disciplinary action.",
  type: "conduct",
  required: true,
};

const SAFEGUARDING_DECLARATION: DeclarationRequirement = {
  id: "safeguarding",
  title: "Safeguarding & Vulnerable Adults Declaration",
  text: "I confirm that I am not subject to any barring decisions by the Disclosure and Barring Service (DBS), and that I have not been cautioned, charged, or convicted of any offence that would make me unsuitable to work with children or vulnerable adults. I understand that an Enhanced DBS check (with barred lists where applicable) will be required as part of this appointment.",
  type: "safeguarding",
  required: true,
};

const CLINICAL_GOVERNANCE_DECLARATION: DeclarationRequirement = {
  id: "clinical_governance",
  title: "Clinical Governance & Professional Standards",
  text: "I confirm that I am currently registered with the relevant regulatory body (HCPC, NMC, or GMC as applicable), that my registration is in good standing, and that I have no current conditions, undertakings, or sanctions on my practice. I agree to notify OpsCom immediately of any change to my registration status, any clinical investigation, fitness-to-practise proceedings, or serious incident that may affect my ability to practise safely.",
  type: "clinical_governance",
  required: true,
};

const HEALTH_DECLARATION: DeclarationRequirement = {
  id: "health",
  title: "Occupational Health Declaration",
  text: "I confirm that I am aware of the physical and psychological demands of this role. I agree to participate in occupational health assessments as required, to disclose any condition that may affect my ability to perform the duties of this role safely, and to seek appropriate support if my health impacts my operational capability.",
  type: "health",
  required: true,
};

const INFOSEC_DECLARATION: DeclarationRequirement = {
  id: "infosec",
  title: "Information Security & Confidentiality",
  text: "I understand that I may have access to sensitive operational, clinical, and personal data during the course of my role. I agree to protect the confidentiality, integrity, and availability of all information I access in accordance with the OpsCom Information Security Policy, NHS Data Security and Protection Toolkit requirements, and relevant legislation including the Computer Misuse Act 1990.",
  type: "information_security",
  required: true,
};

// ─── SHARED DOCUMENTS ────────────────────────────────────────────────────────
const DBS_ENHANCED: RequiredDocument = {
  id: "dbs_enhanced",
  name: "Enhanced DBS Certificate",
  description: "Enhanced Disclosure & Barring Service check including children's and adults' barred lists where applicable.",
  required: true,
  validityYears: 3,
  gdprCategory: "criminal",
  notes: "DBS Update Service subscription preferred. Must be dated within 3 years. Portable DBS accepted if registered on Update Service.",
  verificationRequired: true,
};

const DBS_STANDARD: RequiredDocument = {
  id: "dbs_standard",
  name: "Standard DBS Certificate",
  description: "Standard Disclosure & Barring Service check.",
  required: true,
  validityYears: 3,
  gdprCategory: "criminal",
  verificationRequired: true,
};

const RIGHT_TO_WORK: RequiredDocument = {
  id: "rtw",
  name: "Right to Work in the UK",
  description: "Evidence of legal entitlement to work in the United Kingdom.",
  required: true,
  gdprCategory: "identity",
  notes: "Accepted: UK/EU passport, biometric residence permit, share code, or other valid RTW document.",
  verificationRequired: true,
};

const CV_DOC: RequiredDocument = {
  id: "cv",
  name: "Curriculum Vitae (CV)",
  description: "Full employment history with dates, including any employment gaps.",
  required: true,
  gdprCategory: "employment",
  notes: "Must cover all employment for the past 5 years minimum. All gaps must be explained.",
};

const REFERENCES: RequiredDocument = {
  id: "references",
  name: "Two Professional References",
  description: "Contact details for two professional referees.",
  required: true,
  gdprCategory: "employment",
  notes: "One must be your current or most recent employer. Character references not accepted.",
};

const QUALIFICATION_CERTS: RequiredDocument = {
  id: "qual_certs",
  name: "Qualification Certificates",
  description: "Copies of all relevant qualifications and certifications claimed on your application.",
  required: true,
  gdprCategory: "qualification",
};

const DRIVING_LICENCE: RequiredDocument = {
  id: "driving_licence",
  name: "DVLA Driving Licence",
  description: "Full valid DVLA driving licence.",
  required: true,
  gdprCategory: "identity",
  notes: "Photocard and counterpart. A DVLA licence check may be required.",
  verificationRequired: true,
};

const DRIVING_RESPONSE: RequiredDocument = {
  id: "driving_response",
  name: "Emergency Response Driving Certificate",
  description: "RoSPA/BTEC Level 3 emergency response driving or equivalent qualification.",
  required: false,
  gdprCategory: "qualification",
  notes: "Required for blue light response roles. LANTRA, RoSPA, BTEC, or equivalent accepted.",
};

const INDEMNITY: RequiredDocument = {
  id: "indemnity",
  name: "Professional Indemnity Insurance",
  description: "Evidence of current professional indemnity/medical defence cover.",
  required: true,
  gdprCategory: "professional",
  validityYears: 1,
  notes: "MDU, MDDUS, MPS, or equivalent. Must be current and cover scope of practice.",
  verificationRequired: true,
};

const HCPC_CERT: RequiredDocument = {
  id: "hcpc_cert",
  name: "HCPC Registration Certificate",
  description: "Current HCPC certificate of registration.",
  required: true,
  gdprCategory: "professional",
  validityYears: 2,
  verificationRequired: true,
};

const NMC_CERT: RequiredDocument = {
  id: "nmc_cert",
  name: "NMC PIN Verification",
  description: "NMC registration confirmation or PIN letter.",
  required: true,
  gdprCategory: "professional",
  validityYears: 1,
  verificationRequired: true,
};

const GMC_CERT: RequiredDocument = {
  id: "gmc_cert",
  name: "GMC Registration Certificate",
  description: "GMC registration letter or certificate of good standing.",
  required: true,
  gdprCategory: "professional",
  validityYears: 1,
  verificationRequired: true,
};

// ─── PATHWAY CONFIGS ─────────────────────────────────────────────────────────
export const PATHWAY_CONFIGS: Record<WorkforcePathway, PathwayConfig> = {
  clinical: {
    id: "clinical",
    label: "Clinical",
    description: "Paramedics, doctors, nurses, critical care practitioners, and allied health professionals delivering pre-hospital care, emergency response, and event medical cover.",
    tagline: "Pre-hospital & emergency clinical care",
    icon: "🩺",
    color: "text-blue-400",
    bg: "bg-blue-950/30",
    border: "border-blue-800/60",
    cqcAligned: true,
    roles: ["first_responder","eca","frec3","frec4","technician","paramedic","advanced_paramedic","critical_care_paramedic","nurse","doctor","consultant"],
  },
  fire_rescue: {
    id: "fire_rescue",
    label: "Fire & Rescue",
    description: "Firefighters, rescue technicians, BA operators, water rescue specialists, and fire/rescue operational staff responding to structural, wildfire, and technical rescue incidents.",
    tagline: "Operational fire & technical rescue",
    icon: "🔥",
    color: "text-orange-400",
    bg: "bg-orange-950/30",
    border: "border-orange-800/60",
    cqcAligned: false,
    roles: ["firefighter","fire_oic","ba_operator","water_rescue_technician","rope_rescue_technician","hazmat_technician"],
  },
  event_ops: {
    id: "event_ops",
    label: "Event Operations",
    description: "Event stewards, supervisors, event medics, and commanders supporting mass gatherings, public events, and spectator safety operations.",
    tagline: "Mass gathering & event safety",
    icon: "🎪",
    color: "text-emerald-400",
    bg: "bg-emerald-950/30",
    border: "border-emerald-800/60",
    cqcAligned: false,
    roles: ["event_steward","event_supervisor","event_medic","event_commander"],
  },
  command_control: {
    id: "command_control",
    label: "Command & Control",
    description: "Dispatchers, loggists, bronze/silver/gold commanders, and liaison officers operating within the command structure of multi-agency major incidents.",
    tagline: "Incident command & operational control",
    icon: "📡",
    color: "text-purple-400",
    bg: "bg-purple-950/30",
    border: "border-purple-800/60",
    cqcAligned: false,
    roles: ["dispatcher","loggist","bronze_commander","silver_commander","gold_commander","liaison_officer"],
  },
  specialist_ops: {
    id: "specialist_ops",
    label: "Specialist Operations",
    description: "Specialist practitioners in USAR, swift water rescue, wildfire operations, and CBRN/HAZMAT — requiring advanced technical qualifications and operational clearance.",
    tagline: "USAR · Water Rescue · Wildfire · HAZMAT",
    icon: "⚡",
    color: "text-amber-400",
    bg: "bg-amber-950/30",
    border: "border-amber-800/60",
    cqcAligned: false,
    roles: ["urban_search_rescue","swift_water_rescue","wildfire_specialist","hazmat_specialist"],
  },
};

// ─── ROLE LABEL MAP ───────────────────────────────────────────────────────────
export const ROLE_LABELS: Record<WorkforceRole, string> = {
  first_responder: "First Responder",
  eca: "Emergency Care Assistant (ECA)",
  technician: "Emergency Medical Technician (EMT)",
  frec3: "FREC 3 Responder",
  frec4: "FREC 4 Responder",
  paramedic: "Paramedic",
  advanced_paramedic: "Advanced Paramedic",
  critical_care_paramedic: "Critical Care Paramedic (CCP)",
  nurse: "Registered Nurse",
  doctor: "Doctor / Medical Practitioner",
  consultant: "Consultant Practitioner",
  firefighter: "Firefighter",
  fire_oic: "Fire Officer in Charge (OIC)",
  ba_operator: "BA (Breathing Apparatus) Operator",
  water_rescue_technician: "Water Rescue Technician",
  rope_rescue_technician: "Rope Rescue Technician",
  hazmat_technician: "HAZMAT Technician",
  event_steward: "Event Steward",
  event_supervisor: "Event Supervisor",
  event_medic: "Event Medic",
  event_commander: "Event Safety Commander",
  dispatcher: "Dispatcher / Control Room Operator",
  loggist: "Loggist",
  bronze_commander: "Bronze Commander",
  silver_commander: "Silver Commander",
  gold_commander: "Gold Commander",
  liaison_officer: "Liaison Officer",
  urban_search_rescue: "USAR Technician",
  swift_water_rescue: "Swift Water Rescue Technician",
  wildfire_specialist: "Wildfire Specialist",
  hazmat_specialist: "HAZMAT Specialist",
};

export const ROLE_DESCRIPTIONS: Record<WorkforceRole, string> = {
  first_responder: "Provides immediate response to life-threatening emergencies using BLS skills and AED. Entry-level clinical responder.",
  eca: "Pre-hospital emergency care assistant supporting paramedic crews. Driving and patient care duties.",
  technician: "Registered emergency care technician providing clinical care under JRCALC guidelines.",
  frec3: "FREC Level 3 qualified event medical responder.",
  frec4: "FREC Level 4 qualified event medical practitioner with intermediate clinical skills.",
  paramedic: "HCPC registered paramedic delivering pre-hospital emergency and urgent care.",
  advanced_paramedic: "Advanced practice paramedic with extended scope including critical care skills.",
  critical_care_paramedic: "Critical care paramedic delivering PHEM-level interventions in pre-hospital critical care.",
  nurse: "NMC registered nurse with emergency, critical care, or pre-hospital experience.",
  doctor: "GMC registered medical practitioner working in pre-hospital emergency medicine.",
  consultant: "Senior consultant practitioner providing specialist clinical leadership.",
  firefighter: "Operational firefighter responding to structure fires, RTCs, and rescue incidents.",
  fire_oic: "Fire Officer in Charge responsible for tactical command of fire and rescue operations.",
  ba_operator: "Qualified BA operator for entry into immediately dangerous to life environments.",
  water_rescue_technician: "Flood, swift water, or rescue swimmer technician.",
  rope_rescue_technician: "Rope access and technical rope rescue specialist.",
  hazmat_technician: "Level 2 or 3 HAZMAT technician for chemical, biological, radiological, and nuclear incidents.",
  event_steward: "Spectator safety steward for licensed events and mass gatherings.",
  event_supervisor: "Senior steward supervising event safety operations.",
  event_medic: "Event medical responder providing first aid and basic emergency care.",
  event_commander: "Event Safety Commander responsible for overall event safety planning and operations.",
  dispatcher: "Control room dispatcher coordinating emergency resource deployment.",
  loggist: "Trained loggist maintaining legally defensible records during major incidents.",
  bronze_commander: "Bronze operational commander responsible for on-scene tactical management.",
  silver_commander: "Silver tactical commander coordinating multi-agency response.",
  gold_commander: "Gold strategic commander providing strategic direction for major incidents.",
  liaison_officer: "Inter-agency liaison officer representing the organisation in multi-agency command structures.",
  urban_search_rescue: "USAR trained technician for collapsed structure search and rescue.",
  swift_water_rescue: "Swift water and flood rescue technician.",
  wildfire_specialist: "Wildfire operations specialist including fire behaviour and suppressant tactics.",
  hazmat_specialist: "Senior HAZMAT specialist with CBRN and decontamination expertise.",
};

export const ROLE_RISK_LEVELS: Record<WorkforceRole, OperationalRiskLevel> = {
  first_responder: "medium",
  eca: "medium",
  technician: "medium",
  frec3: "low",
  frec4: "medium",
  paramedic: "high",
  advanced_paramedic: "high",
  critical_care_paramedic: "critical",
  nurse: "high",
  doctor: "high",
  consultant: "high",
  firefighter: "critical",
  fire_oic: "critical",
  ba_operator: "critical",
  water_rescue_technician: "critical",
  rope_rescue_technician: "critical",
  hazmat_technician: "critical",
  event_steward: "low",
  event_supervisor: "low",
  event_medic: "medium",
  event_commander: "medium",
  dispatcher: "medium",
  loggist: "medium",
  bronze_commander: "high",
  silver_commander: "high",
  gold_commander: "critical",
  liaison_officer: "medium",
  urban_search_rescue: "critical",
  swift_water_rescue: "critical",
  wildfire_specialist: "critical",
  hazmat_specialist: "critical",
};

export const ROLE_VET_LEVELS: Record<WorkforceRole, VetLevel> = {
  first_responder: "enhanced_dbs",
  eca: "enhanced_dbs",
  technician: "enhanced_dbs",
  frec3: "standard_dbs",
  frec4: "enhanced_dbs",
  paramedic: "enhanced_barred",
  advanced_paramedic: "enhanced_barred",
  critical_care_paramedic: "enhanced_barred",
  nurse: "enhanced_barred",
  doctor: "enhanced_barred",
  consultant: "enhanced_barred",
  firefighter: "enhanced_dbs",
  fire_oic: "enhanced_dbs",
  ba_operator: "enhanced_dbs",
  water_rescue_technician: "enhanced_dbs",
  rope_rescue_technician: "enhanced_dbs",
  hazmat_technician: "enhanced_dbs",
  event_steward: "basic_dbs",
  event_supervisor: "standard_dbs",
  event_medic: "enhanced_dbs",
  event_commander: "standard_dbs",
  dispatcher: "standard_dbs",
  loggist: "standard_dbs",
  bronze_commander: "standard_dbs",
  silver_commander: "standard_dbs",
  gold_commander: "sc",
  liaison_officer: "standard_dbs",
  urban_search_rescue: "enhanced_dbs",
  swift_water_rescue: "enhanced_dbs",
  wildfire_specialist: "enhanced_dbs",
  hazmat_specialist: "enhanced_dbs",
};

// ─── REGISTRATION REQUIREMENTS ───────────────────────────────────────────────
export const ROLE_REGISTRATION: Partial<Record<WorkforceRole, RegistrationRequirement>> = {
  paramedic: {
    body: "HCPC",
    bodyLabel: "Health & Care Professions Council (HCPC)",
    numberLabel: "HCPC PIN",
    expiryRequired: true,
    verificationUrl: "https://www.hcpc-uk.org/check-the-register/",
    notes: "Registration must be in good standing with no conditions on practice.",
  },
  advanced_paramedic: {
    body: "HCPC",
    bodyLabel: "Health & Care Professions Council (HCPC)",
    numberLabel: "HCPC PIN",
    expiryRequired: true,
    verificationUrl: "https://www.hcpc-uk.org/check-the-register/",
  },
  critical_care_paramedic: {
    body: "HCPC",
    bodyLabel: "Health & Care Professions Council (HCPC)",
    numberLabel: "HCPC PIN",
    expiryRequired: true,
    verificationUrl: "https://www.hcpc-uk.org/check-the-register/",
  },
  nurse: {
    body: "NMC",
    bodyLabel: "Nursing and Midwifery Council (NMC)",
    numberLabel: "NMC PIN",
    expiryRequired: true,
    verificationUrl: "https://www.nmc.org.uk/registration/search-the-register/",
  },
  doctor: {
    body: "GMC",
    bodyLabel: "General Medical Council (GMC)",
    numberLabel: "GMC Reference Number",
    expiryRequired: false,
    verificationUrl: "https://www.gmc-uk.org/registration-and-licensing/the-medical-register",
    notes: "Revalidation cycle and responsible officer details required.",
  },
  consultant: {
    body: "GMC",
    bodyLabel: "General Medical Council (GMC)",
    numberLabel: "GMC Reference Number",
    expiryRequired: false,
    verificationUrl: "https://www.gmc-uk.org/registration-and-licensing/the-medical-register",
  },
  technician: {
    body: "IHCD/BTEC",
    bodyLabel: "IHCD / Awarding Body",
    numberLabel: "Certificate Number",
    expiryRequired: true,
  },
};

// ─── DOCUMENTS PER ROLE ───────────────────────────────────────────────────────
export function getDocumentsForRole(role: WorkforceRole): RequiredDocument[] {
  const base: RequiredDocument[] = [RIGHT_TO_WORK, CV_DOC, REFERENCES];

  const map: Partial<Record<WorkforceRole, RequiredDocument[]>> = {
    paramedic:                [DBS_ENHANCED, HCPC_CERT, DRIVING_LICENCE, DRIVING_RESPONSE, QUALIFICATION_CERTS, ...base],
    advanced_paramedic:       [DBS_ENHANCED, HCPC_CERT, DRIVING_LICENCE, DRIVING_RESPONSE, QUALIFICATION_CERTS, ...base],
    critical_care_paramedic:  [DBS_ENHANCED, HCPC_CERT, DRIVING_LICENCE, DRIVING_RESPONSE, QUALIFICATION_CERTS, INDEMNITY, ...base],
    doctor:                   [DBS_ENHANCED, GMC_CERT, INDEMNITY, QUALIFICATION_CERTS, ...base],
    consultant:               [DBS_ENHANCED, GMC_CERT, INDEMNITY, QUALIFICATION_CERTS, ...base],
    nurse:                    [DBS_ENHANCED, NMC_CERT, QUALIFICATION_CERTS, ...base],
    technician:               [DBS_ENHANCED, DRIVING_LICENCE, QUALIFICATION_CERTS, ...base],
    eca:                      [DBS_ENHANCED, DRIVING_LICENCE, QUALIFICATION_CERTS, ...base],
    frec4:                    [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    frec3:                    [DBS_STANDARD, QUALIFICATION_CERTS, ...base],
    first_responder:          [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    firefighter:              [DBS_ENHANCED, DRIVING_LICENCE, QUALIFICATION_CERTS, ...base],
    fire_oic:                 [DBS_ENHANCED, DRIVING_LICENCE, QUALIFICATION_CERTS, ...base],
    ba_operator:              [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    water_rescue_technician:  [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    rope_rescue_technician:   [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    hazmat_technician:        [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    event_steward:            [DBS_STANDARD, RIGHT_TO_WORK, CV_DOC],
    event_supervisor:         [DBS_STANDARD, ...base],
    event_medic:              [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    event_commander:          [DBS_STANDARD, QUALIFICATION_CERTS, ...base],
    dispatcher:               [DBS_STANDARD, ...base],
    loggist:                  [DBS_STANDARD, QUALIFICATION_CERTS, ...base],
    bronze_commander:         [DBS_STANDARD, QUALIFICATION_CERTS, ...base],
    silver_commander:         [DBS_STANDARD, QUALIFICATION_CERTS, ...base],
    gold_commander:           [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    liaison_officer:          [DBS_STANDARD, ...base],
    urban_search_rescue:      [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    swift_water_rescue:       [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    wildfire_specialist:      [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
    hazmat_specialist:        [DBS_ENHANCED, QUALIFICATION_CERTS, ...base],
  };

  return map[role] ?? [...base, DBS_STANDARD];
}

// ─── COMPETENCIES PER ROLE ────────────────────────────────────────────────────
export function getCompetenciesForRole(role: WorkforceRole): CompetencyRequirement[] {
  const BLUE_LIGHT_DRIVING: CompetencyRequirement = { id: "blue_light", label: "Blue Light / Emergency Response Driving", category: "driving", required: false, expiryYears: 3 };
  const SAFEGUARDING_L2: CompetencyRequirement = { id: "safeguarding_l2", label: "Safeguarding Adults & Children Level 2", category: "safeguarding", required: true, expiryYears: 3 };
  const SAFEGUARDING_L3: CompetencyRequirement = { id: "safeguarding_l3", label: "Safeguarding Adults & Children Level 3", category: "safeguarding", required: true, expiryYears: 3 };
  const MANUAL_HANDLING: CompetencyRequirement = { id: "manual_handling", label: "Manual Handling (Clinical)", category: "operational", required: true, expiryYears: 1 };
  const INFECTION_CTRL: CompetencyRequirement = { id: "infection_control", label: "Infection Prevention & Control", category: "clinical", required: true, expiryYears: 1 };
  const ILS: CompetencyRequirement = { id: "ils", label: "Immediate Life Support (ILS)", category: "clinical", required: false };
  const ALS: CompetencyRequirement = { id: "als", label: "Advanced Life Support (ALS)", category: "clinical", required: false };
  const PHEM: CompetencyRequirement = { id: "phem", label: "Pre-Hospital Emergency Medicine (PHEM)", category: "clinical", required: false };
  const JESIP: CompetencyRequirement = { id: "jesip", label: "JESIP Joint Decision Model", category: "command", required: false, expiryYears: 3 };
  const INCIDENT_CMD: CompetencyRequirement = { id: "incident_cmd", label: "Incident Command (ICS/NILO)", category: "command", required: false };
  const LOGGIST_QUAL: CompetencyRequirement = { id: "loggist_qual", label: "Accredited Loggist Qualification", category: "command", required: true };
  const BA_QUAL: CompetencyRequirement = { id: "ba_qual", label: "Breathing Apparatus (BA) Qualification", category: "rescue", required: true };
  const WATER_RESCUE: CompetencyRequirement = { id: "water_rescue", label: "Water Rescue Technician", category: "rescue", required: true };
  const ROPE_RESCUE: CompetencyRequirement = { id: "rope_rescue", label: "Rope Rescue / Technical Rescue", category: "rescue", required: true };
  const HAZMAT_L2: CompetencyRequirement = { id: "hazmat_l2", label: "HAZMAT Level 2 Technician", category: "technical", required: true };
  const RADIO_COMMS: CompetencyRequirement = { id: "radio", label: "Operational Radio Communications (Airwave/TETRA)", category: "communication", required: true };

  const map: Partial<Record<WorkforceRole, CompetencyRequirement[]>> = {
    paramedic:               [BLUE_LIGHT_DRIVING, SAFEGUARDING_L3, MANUAL_HANDLING, INFECTION_CTRL, ILS, RADIO_COMMS],
    advanced_paramedic:      [BLUE_LIGHT_DRIVING, SAFEGUARDING_L3, MANUAL_HANDLING, INFECTION_CTRL, ALS, PHEM, RADIO_COMMS],
    critical_care_paramedic: [BLUE_LIGHT_DRIVING, SAFEGUARDING_L3, MANUAL_HANDLING, INFECTION_CTRL, ALS, PHEM, RADIO_COMMS],
    doctor:                  [SAFEGUARDING_L3, MANUAL_HANDLING, INFECTION_CTRL, ALS, PHEM],
    consultant:              [SAFEGUARDING_L3, MANUAL_HANDLING, INFECTION_CTRL, ALS, PHEM],
    nurse:                   [SAFEGUARDING_L3, MANUAL_HANDLING, INFECTION_CTRL, ILS],
    technician:              [BLUE_LIGHT_DRIVING, SAFEGUARDING_L2, MANUAL_HANDLING, INFECTION_CTRL, RADIO_COMMS],
    eca:                     [BLUE_LIGHT_DRIVING, SAFEGUARDING_L2, MANUAL_HANDLING, INFECTION_CTRL, RADIO_COMMS],
    first_responder:         [SAFEGUARDING_L2, MANUAL_HANDLING, INFECTION_CTRL],
    frec3:                   [SAFEGUARDING_L2, MANUAL_HANDLING],
    frec4:                   [SAFEGUARDING_L2, MANUAL_HANDLING, INFECTION_CTRL],
    firefighter:             [BA_QUAL, JESIP, RADIO_COMMS, MANUAL_HANDLING],
    fire_oic:                [BA_QUAL, JESIP, INCIDENT_CMD, RADIO_COMMS, MANUAL_HANDLING],
    ba_operator:             [BA_QUAL, RADIO_COMMS, MANUAL_HANDLING],
    water_rescue_technician: [WATER_RESCUE, JESIP, RADIO_COMMS, MANUAL_HANDLING],
    rope_rescue_technician:  [ROPE_RESCUE, JESIP, RADIO_COMMS, MANUAL_HANDLING],
    hazmat_technician:       [HAZMAT_L2, JESIP, RADIO_COMMS, MANUAL_HANDLING],
    event_steward:           [SAFEGUARDING_L2, MANUAL_HANDLING],
    event_supervisor:        [SAFEGUARDING_L2, MANUAL_HANDLING, RADIO_COMMS],
    event_medic:             [SAFEGUARDING_L2, MANUAL_HANDLING, INFECTION_CTRL],
    event_commander:         [SAFEGUARDING_L2, JESIP, INCIDENT_CMD, RADIO_COMMS],
    dispatcher:              [JESIP, RADIO_COMMS],
    loggist:                 [LOGGIST_QUAL, JESIP],
    bronze_commander:        [JESIP, INCIDENT_CMD, RADIO_COMMS],
    silver_commander:        [JESIP, INCIDENT_CMD, RADIO_COMMS],
    gold_commander:          [JESIP, INCIDENT_CMD, RADIO_COMMS],
    liaison_officer:         [JESIP, RADIO_COMMS],
    urban_search_rescue:     [ROPE_RESCUE, HAZMAT_L2, JESIP, RADIO_COMMS],
    swift_water_rescue:      [WATER_RESCUE, JESIP, RADIO_COMMS],
    wildfire_specialist:     [JESIP, RADIO_COMMS],
    hazmat_specialist:       [HAZMAT_L2, JESIP, RADIO_COMMS],
  };

  return map[role] ?? [SAFEGUARDING_L2, MANUAL_HANDLING];
}

// ─── DECLARATIONS PER ROLE ────────────────────────────────────────────────────
export function getDeclarationsForRole(role: WorkforceRole): DeclarationRequirement[] {
  const base = [GDPR_DECLARATION, ACCURACY_DECLARATION, CONDUCT_DECLARATION, INFOSEC_DECLARATION];
  const clinicalExtras = [...base, CLINICAL_GOVERNANCE_DECLARATION, SAFEGUARDING_DECLARATION, HEALTH_DECLARATION];
  const operationalExtras = [...base, SAFEGUARDING_DECLARATION, HEALTH_DECLARATION];

  const clinical: WorkforceRole[] = ["paramedic","advanced_paramedic","critical_care_paramedic","doctor","consultant","nurse","technician","eca","first_responder","frec3","frec4"];
  const operational: WorkforceRole[] = ["firefighter","fire_oic","ba_operator","water_rescue_technician","rope_rescue_technician","hazmat_technician","urban_search_rescue","swift_water_rescue","wildfire_specialist","hazmat_specialist"];

  if (clinical.includes(role)) return clinicalExtras;
  if (operational.includes(role)) return operationalExtras;
  return [...base, HEALTH_DECLARATION];
}

// ─── ONBOARDING STAGES ────────────────────────────────────────────────────────
export function getOnboardingStages(role: WorkforceRole): OnboardingStage[] {
  const riskLevel = ROLE_RISK_LEVELS[role];
  const isClinical = ["paramedic","advanced_paramedic","critical_care_paramedic","doctor","consultant","nurse"].includes(role);
  const isCommand = ["bronze_commander","silver_commander","gold_commander"].includes(role);

  const stages: OnboardingStage[] = [
    { id: "application",    label: "Application Received",         description: "Application submitted and reference number assigned.",                 approvalRequired: false },
    { id: "doc_review",     label: "Document Review",              description: "Documents verified by recruitment team.",                               approvalRequired: true, approver: "Recruitment Lead" },
    { id: "reg_check",      label: "Registration Verification",    description: "Professional registration verified with regulatory body.",              approvalRequired: true, approver: "Compliance Officer" },
    { id: "dbs",            label: "DBS Clearance",                description: "DBS check initiated and cleared.",                                      approvalRequired: true, approver: "HR Manager" },
    { id: "interview",      label: "Interview & Assessment",       description: "Competency-based interview or assessment centre.",                       approvalRequired: true, approver: "Operations Manager" },
  ];

  if (isClinical) {
    stages.push(
      { id: "clin_governance", label: "Clinical Governance Review",   description: "Clinical governance lead reviews clinical competencies and references.", approvalRequired: true, approver: "Clinical Governance Lead" },
      { id: "occ_health",      label: "Occupational Health",          description: "Occupational health assessment completed.",                             approvalRequired: true, approver: "OH Advisor" },
    );
  }

  if (riskLevel === "critical" || isCommand) {
    stages.push({ id: "command_approval", label: "Command Approval", description: "Senior command approves deployment permissions and operational clearance.", approvalRequired: true, approver: "Director" });
  }

  stages.push(
    { id: "induction",      label: "Induction",                    description: "Mandatory induction completed.",                                        approvalRequired: true, approver: "Training Officer" },
    { id: "activated",      label: "Operationally Active",         description: "Staff member is live and cleared for deployment.",                       approvalRequired: false },
  );

  return stages;
}

// ─── OPERATIONAL PERMISSIONS ──────────────────────────────────────────────────
export function getPermissionsForRole(role: WorkforceRole): OperationalPermissions {
  const commandRoles: WorkforceRole[] = ["bronze_commander","silver_commander","gold_commander"];
  const clinicalRoles: WorkforceRole[] = ["paramedic","advanced_paramedic","critical_care_paramedic","doctor","consultant","nurse","technician","eca","first_responder"];
  const dispatchRoles: WorkforceRole[] = ["dispatcher","loggist"];

  return {
    cadAccess:         [...clinicalRoles,...commandRoles,...dispatchRoles].includes(role),
    mdtAccess:         [...clinicalRoles,"firefighter","fire_oic"].includes(role),
    fleetAccess:       [...clinicalRoles,"firefighter","eca","technician"].includes(role),
    commandAccess:     commandRoles.includes(role),
    commandTier:       role === "gold_commander" ? "gold" : role === "silver_commander" ? "silver" : commandRoles.includes(role) ? "bronze" : undefined,
    deploymentEligible: !["dispatcher","loggist","liaison_officer"].includes(role),
    schedulingAccess:  true,
    clinicalAuditAccess: [...clinicalRoles,"dispatcher"].includes(role),
  };
}

// ─── COMPLIANCE SCORE BASELINE ────────────────────────────────────────────────
export function getComplianceBaselineScore(role: WorkforceRole): number {
  const risk = ROLE_RISK_LEVELS[role];
  return { low: 60, medium: 75, high: 85, critical: 95 }[risk];
}

// ─── VET LEVEL LABELS ────────────────────────────────────────────────────────
export const VET_LEVEL_LABELS: Record<VetLevel, string> = {
  basic_dbs:       "Basic DBS",
  standard_dbs:    "Standard DBS",
  enhanced_dbs:    "Enhanced DBS",
  enhanced_barred: "Enhanced DBS + Barred Lists",
  sc:              "Security Clearance (SC)",
  dv:              "Developed Vetting (DV)",
};

export const RISK_LEVEL_LABELS: Record<OperationalRiskLevel, string> = {
  low:      "Low Risk",
  medium:   "Medium Risk",
  high:     "High Risk",
  critical: "Critical Risk",
};

export const RISK_LEVEL_COLORS: Record<OperationalRiskLevel, string> = {
  low:      "text-emerald-400 border-emerald-800 bg-emerald-950/30",
  medium:   "text-amber-400 border-amber-800 bg-amber-950/30",
  high:     "text-orange-400 border-orange-800 bg-orange-950/30",
  critical: "text-red-400 border-red-800 bg-red-950/30",
};
